import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useEffect, useState } from 'react'
import { db } from '@/services/firebase/config'
import { useAuth } from '@/store/auth/useAuth'
import type { Ingredient, IngredientGroup } from '@/types/ingredients'

const STORAGE_KEY = 'parsley:ingredients_cache'
const VERSION_KEY = 'parsley:ingredients_version'

export interface IngredientsRefContextValue {
  ingredients: Ingredient[]
  ingredientGroups: IngredientGroup[]
  loading: boolean
  error: string | null
}

export const IngredientsRefContext = createContext<IngredientsRefContextValue | undefined>(undefined)

interface CachedData {
  ingredients: Ingredient[]
  ingredientGroups: IngredientGroup[]
}

function loadFromCache(): CachedData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as CachedData
  } catch {
    return null
  }
}

function saveToCache(data: CachedData, version: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    localStorage.setItem(VERSION_KEY, version)
  } catch {
    // localStorage full or unavailable — silently continue without caching
  }
}

function getCachedVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_KEY)
  } catch {
    return null
  }
}

export function IngredientsRefProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, initialized: authInitialized } = useAuth()
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [ingredientGroups, setIngredientGroups] = useState<IngredientGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFromFirestore = useCallback(async (version: string) => {
    const [ingredientSnap, groupSnap] = await Promise.all([
      getDocs(collection(db, 'ingredients')),
      getDocs(collection(db, 'ingredient_groups'))
    ])

    const fetchedIngredients = ingredientSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Ingredient)
    const fetchedGroups = groupSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as IngredientGroup)

    setIngredients(fetchedIngredients)
    setIngredientGroups(fetchedGroups)
    saveToCache({ ingredients: fetchedIngredients, ingredientGroups: fetchedGroups }, version)
  }, [])

  useEffect(() => {
    if (!authInitialized || !isAuthenticated) {
      setIngredients([])
      setIngredientGroups([])
      setLoading(false)
      return
    }

    // Load cached data immediately so the UI has something while we check the version
    const cached = loadFromCache()
    if (cached) {
      setIngredients(cached.ingredients)
      setIngredientGroups(cached.ingredientGroups)
      setLoading(false)
    }

    // Check version in the background
    const checkVersion = async () => {
      try {
        const versionDoc = await getDoc(doc(db, 'metadata', 'ingredients_version'))
        const remoteVersion = versionDoc.exists() ? (versionDoc.data().version as string) : ''
        const cachedVersion = getCachedVersion()

        if (cachedVersion === remoteVersion && cached) {
          // Cache is up to date — already loaded above
          return
        }

        // Cache is stale or missing — fetch fresh data
        if (!cached) setLoading(true)
        await fetchFromFirestore(remoteVersion)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load ingredients')
      } finally {
        setLoading(false)
      }
    }

    checkVersion()
  }, [authInitialized, isAuthenticated, fetchFromFirestore])

  return (
    <IngredientsRefContext.Provider value={{ ingredients, ingredientGroups, loading, error }}>{children}</IngredientsRefContext.Provider>
  )
}
